/**
 * How `/fly`'s picture is made: the WebGPU renderer, the camera, and where
 * that camera sits each frame. Split out of `flight-scene.ts`, which owns the
 * loop, the sim and the scene graph — this file owns nothing that survives a
 * frame except the two three.js objects it builds.
 */

import * as THREE from "three/webgpu";
import type { Pose } from "@red-opus/engine";
import type { ViewMetrics } from "../three/view-metrics.ts";
import { viewPixelRatio } from "../three/view-pixel-ratio.ts";
import type { RendererMode } from "../three/renderer-mode.ts";
import { disableWebGlFallback } from "../three/webgpu-support.ts";
import {
  CHASE_CAMERA,
  chaseCameraGoal,
  chaseCameraTarget,
  flyByAnchor,
  needsNewFlyByAnchor,
  smoothFollow,
  zoomedFlyByCameraSpec,
} from "./camera-follow.ts";
import { COCKPIT_3D_NEAR_M } from "./cockpit-dash-quad.ts";
import { applyCockpitShear } from "./cockpit-projection.ts";
import { cockpitCameraView, LOOK_AHEAD, type EyeOffset, type LookAim } from "./cockpit-view.ts";
import type { GlanceDirection } from "./keys.ts";

export const CAMERA_FOV_DEG = 55;
export const CAMERA_NEAR_M = 1;
export const CAMERA_FAR_M = 26000;

/**
 * Reversed-Z depth (user report, 2026-08-19: "the whole world flickers
 * including the wireframe", flat terrain "effectively invisible").
 *
 * A 1 m near plane and a 26 km far plane is a 26,000:1 range, and a
 * conventional depth buffer spends almost all of its precision in the first
 * few metres: at 10 km the resolution of a 24-bit `1/z` buffer here is
 * `z^2 / (near * 2^24)` ~ **6 m**, while the ground plane, the `TERR`
 * field/river/road polygons at datum and the survey grid live inside a
 * *fraction of a metre* of each other (`world.ts`). Every one of those layers
 * therefore resolved to the same depth value at range and won the test at
 * random per frame — which is the shimmer, and which is also why the flat
 * terrain colouring vanished under the ground plane instead of lying on it.
 *
 * Reversed-Z with a float depth buffer inverts that distribution and gives
 * near-uniform *relative* precision across the whole range (~1e-6 of z, i.e.
 * ~1 cm at 10 km), which separates all three layers everywhere they are
 * drawn. It is a renderer flag rather than a shader hack — three's WebGPU
 * backend implements it natively (`reversedDepthBuffer` in
 * `renderers/common/Renderer.js`) — so nothing in the scene has to know about
 * it. `logarithmicDepthBuffer` is the fallback if a driver ever refuses the
 * float depth format; it fixes the same problem with a per-vertex depth write
 * instead.
 */
export const REVERSED_DEPTH_BUFFER = true;

/**
 * `nativeResolution` is ENHANCED MODELS (`effects-settings.ts`), which owns the
 * drawing buffer's scale as well as the geometry — see `view-pixel-ratio.ts` for
 * what the cap it lifts actually costs, and `scene-boot.ts`'s
 * `applyEffectsSettings` for the live half.
 */
/**
 * One renderer on one backend, with nothing decided in here.
 *
 * **`mode` is an argument now rather than a query-string read**, and the move is
 * the whole shape of the WebGL-fallback feature: this function is called
 * *twice* on a machine whose WebGPU boot fails — once for the attempt and once
 * for the replacement — so it cannot be the place that decides which. The
 * decision is `three/renderer-mode.ts`'s (four inputs: the query pin, the
 * stored record, `navigator.gpu`, and whether a fallback is allowed) and the
 * retry is `three/renderer-open.ts`'s.
 *
 * `disableWebGlFallback` still runs on every WebGPU build, and that is not a
 * contradiction now that the fallback is automatic — see its own doc: it is what
 * turns three's *silent* backend swap into the rejection this port catches,
 * names and persists.
 */
export function buildRenderer(
  container: HTMLDivElement,
  nativeResolution: boolean,
  mode: RendererMode,
): THREE.WebGPURenderer {
  const forceWebGL = mode === "webgl";
  const renderer = new THREE.WebGPURenderer({ antialias: true, reversedDepthBuffer: REVERSED_DEPTH_BUFFER, forceWebGL });
  if (!forceWebGL) disableWebGlFallback(renderer);
  renderer.setPixelRatio(viewPixelRatio(nativeResolution));
  // `replaceChildren`, not `appendChild`, and the difference is a black screen.
  //
  // The host (`app.css`'s `.flight__canvas-host`) is `inset: 0` and gives every
  // canvas inside it `display: block; width: 100%; height: 100%`. Two canvases
  // in there therefore do not overlap — they *stack*, the first covering the
  // viewport and the second landing a full viewport-height below the fold. On
  // 2026-08-27 a boot that built the renderer twice left the first, unsized
  // (three's default 300x150 drawing buffer, never `setSize`d because
  // `resizeViewport` only ever touches `state.renderer`) on top of the live one,
  // and the game rendered perfectly into a canvas nobody could see: mean luma
  // 9.63 against a healthy 82.16, with a fully live HUD over it.
  //
  // `openRenderer` no longer permits that double build, so this line is the
  // second lock rather than the fix. It is safe because this host has no other
  // children by construction — `FlightSurface.tsx` renders the div empty and
  // hands it over by `ref`, and the only other canvas on the route
  // (`CockpitPanel.tsx`) is a sibling of the host, not a child of it.
  container.replaceChildren(renderer.domElement);
  return renderer;
}

/**
 * three's `Renderer` drawing-buffer size before anything calls `setSize`
 * (`renderers/common/Renderer.js` seeds `_width = 300`, `_height = 150` from the
 * HTML canvas default).
 *
 * A flight viewport still reporting these after the boot's `resizeViewport` has
 * run is not a plausible window — the host is `inset: 0` on a full-page route —
 * it is a canvas nothing ever sized, which is what an abandoned renderer looks
 * like and what the pixels of the 2026-08-27 black screen actually were. Named
 * here so `scene-boot.ts` can say so out loud instead of drawing into it.
 */
export const UNSIZED_DRAWING_BUFFER_PX = { width: 300, height: 150 } as const;

/** Whether a drawing buffer is still at three's never-sized default. See `UNSIZED_DRAWING_BUFFER_PX`. */
export function isUnsizedDrawingBuffer(widthPx: number, heightPx: number): boolean {
  return widthPx === UNSIZED_DRAWING_BUFFER_PX.width && heightPx === UNSIZED_DRAWING_BUFFER_PX.height;
}

export function buildCamera(): THREE.PerspectiveCamera {
  return new THREE.PerspectiveCamera(CAMERA_FOV_DEG, 1, CAMERA_NEAR_M, CAMERA_FAR_M);
}

/**
 * Resizes the drawing buffer and records the new viewport on the rig.
 *
 * The projection itself is left to `placeCamera`, which runs every frame and
 * is the only place that knows whether this frame is a cockpit view (and
 * therefore whether the principal point belongs on the painted gunsight or in
 * the middle of the window) — see `cockpit-projection.ts`.
 *
 * Takes **measured metrics** rather than the container, so that every buffer
 * sized against this view is sized against one measurement taken at one moment
 * (`three/view-metrics.ts`). It used to read `container.clientWidth` here and
 * `scene-boot.ts` read it again two lines later for the dash quad, which is two
 * forced layouts and two chances to disagree.
 */
export function resizeView(renderer: THREE.WebGPURenderer, rig: CameraRig, metrics: ViewMetrics): void {
  // `false` — the CSS box is the input here, not the output: `app.css` sizes the
  // element at `width: 100%` of an `inset: 0` host, and letting three write
  // inline styles from these numbers would make the element's size depend on
  // the measurement taken *from* it.
  renderer.setSize(metrics.widthCssPx, metrics.heightCssPx, false);
  rig.viewportWidthPx = metrics.widthCssPx;
  rig.viewportHeightPx = metrics.heightCssPx;
}

/** The mutable placement state the three views share: where the chase camera has smoothed to, and where the fly-by is anchored. */
export interface CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  readonly chasePosition: THREE.Vector3;
  /**
   * Pose frozen at the last fly-by replant. `placeFlyBy` recomputes
   * `flyByAnchor` from this every frame rather than caching the resulting
   * point, which is what lets a live wheel zoom (`camera-follow.ts`'s
   * `FLY_BY_ZOOM`) rescale the shot without waiting for the next replant —
   * the point moves with zoom, the pose it's measured from doesn't.
   */
  flyByAnchorPose: Pose;
  /** Smoothed 0.3x-3x zoom factor on the fly-by anchor's placement distance. */
  flyByZoom: number;
  /**
   * The view's current size in **CSS pixels**, kept here by `resizeView`
   * because the cockpit's principal point is a *screen* position — where the
   * painted panel presents its gunsight — and `placeCamera` has to recompute it
   * on every resize (`cockpit-projection.ts`).
   *
   * CSS pixels rather than drawing-buffer pixels, and that distinction now has
   * teeth: `view-pixel-ratio.ts` lets ENHANCED MODELS change how many device
   * pixels a CSS pixel is drawn with, and neither the camera's aspect ratio nor
   * the painted panel's gunsight moves when it does — the panel is a DOM
   * overlay laid out in the same CSS box. A cockpit whose optical axis shifted
   * when the buffer got sharper would be the bug; taking the container's own
   * box, as this does, is why it cannot happen.
   */
  viewportWidthPx: number;
  viewportHeightPx: number;
}

export interface CameraPlacement {
  readonly mode: "cockpit" | "chase" | "flyBy";
  readonly pose: Pose;
  readonly dt: number;
  readonly glance: GlanceDirection;
  /**
   * Right-mouse free look's offset from dead ahead (`free-look.ts`), or
   * `LOOK_AHEAD` when nobody is looking around. Cockpit only — the two external
   * views have their own framing rules and a head turning inside the aeroplane
   * does not move either of them.
   */
  readonly aim?: LookAim;
  readonly eyeOffset: EyeOffset;
  /** Terrain height under the aircraft, so a cockpit on the ground never sinks below it. Datum when omitted. */
  readonly groundHeightM?: number;
  /**
   * Whether this cockpit frame is 3D COCKPIT's — which pulls the near plane in
   * from `CAMERA_NEAR_M` to `COCKPIT_3D_NEAR_M`, because the whole cockpit of a
   * Sopwith Camel is inside the ordinary one (`cockpit-dash-quad.ts`). Ignored
   * outside cockpit mode, where nothing is ever that close to the lens.
   */
  readonly cockpit3d?: boolean;
}

/** `camera.near`, and whether it moved — a changed near plane needs the projection matrix rebuilt. */
function applyNearPlane(camera: THREE.PerspectiveCamera, nearM: number): boolean {
  if (camera.near === nearM) return false;
  camera.near = nearM;
  return true;
}

function placeCockpit(rig: CameraRig, placement: CameraPlacement): void {
  const view = cockpitCameraView(
    placement.pose,
    placement.glance,
    placement.eyeOffset,
    placement.groundHeightM ?? 0,
    placement.aim ?? LOOK_AHEAD,
  );
  rig.camera.up.set(view.up.x, view.up.y, view.up.z);
  rig.camera.position.set(view.position.x, view.position.y, view.position.z);
  rig.camera.lookAt(view.lookAt.x, view.lookAt.y, view.lookAt.z);
}

function placeFlyBy(rig: CameraRig, pose: Pose): void {
  const spec = zoomedFlyByCameraSpec(rig.flyByZoom);
  let anchor = flyByAnchor(rig.flyByAnchorPose, spec);
  if (needsNewFlyByAnchor(anchor, pose.position, spec)) {
    rig.flyByAnchorPose = pose;
    anchor = flyByAnchor(rig.flyByAnchorPose, spec);
  }
  rig.camera.position.set(anchor.x, anchor.y, anchor.z);
  rig.camera.lookAt(pose.position.x, pose.position.y, pose.position.z);
}

function placeChase(rig: CameraRig, pose: Pose, dt: number): void {
  const goal = chaseCameraGoal(pose);
  const smoothed = smoothFollow(
    { x: rig.chasePosition.x, y: rig.chasePosition.y, z: rig.chasePosition.z },
    goal,
    CHASE_CAMERA.followRate,
    dt,
  );
  rig.chasePosition.set(smoothed.x, smoothed.y, smoothed.z);
  rig.camera.position.copy(rig.chasePosition);
  const target = chaseCameraTarget(pose);
  rig.camera.lookAt(target.x, target.y, target.z);
}

/** Points the camera for this frame's view mode. */
export function placeCamera(rig: CameraRig, placement: CameraPlacement): void {
  // The near plane before the shear, so a shear that rebuilds the projection
  // matrix picks the new one up; the flag below covers the frame where only the
  // near plane moved and `applyCockpitShear` therefore early-outs.
  const nearMoved = applyNearPlane(
    rig.camera,
    placement.mode === "cockpit" && placement.cockpit3d === true ? COCKPIT_3D_NEAR_M : CAMERA_NEAR_M,
  );
  // The projection next: a cockpit frame puts the optical axis on the painted
  // gunsight, every other view puts it in the middle of the window.
  applyCockpitShear(
    rig.camera,
    rig.viewportWidthPx,
    rig.viewportHeightPx,
    CAMERA_FOV_DEG,
    placement.mode === "cockpit",
  );
  if (nearMoved) rig.camera.updateProjectionMatrix();
  if (placement.mode === "cockpit") {
    placeCockpit(rig, placement);
    return;
  }
  // The two external views keep the camera's own up on world-up (never
  // rolls) — restore it before using either, in case cockpit view just left
  // it banked from the aircraft's own attitude.
  rig.camera.up.set(0, 1, 0);
  if (placement.mode === "flyBy") placeFlyBy(rig, placement.pose);
  else placeChase(rig, placement.pose, placement.dt);
}
